import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AccessibilityService {
  public prefersReducedMotion: boolean;
  private ringEl: HTMLDivElement;

  constructor() {
    this.prefersReducedMotion = false;
  }

  initializeReducedMotion(): void {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.prefersReducedMotion = mediaQuery.matches;

    // Listen for changes
    mediaQuery.addEventListener('change', (event) => {
      this.prefersReducedMotion = event.matches;
    });
  }

  //COLOR CONTRAST SECTION
  /**
   * Determines the appropriate color (black or white) for accessibility.
   * @param input - The hex color string OR the css variable name of the background
   * @returns 'black' or 'white' for the text color.
   */
  public getAccessibleColor(input: string): 'black' | 'white' {
    // resolve variables and trim
    let value = input?.trim();
    if (!value) return 'black';

    if (value.startsWith('--')) value = this.getCssVariableValue(value).trim();
    else if (value.startsWith('$')) value = this.getCssVariableValue(value.replace('$', '--')).trim();

    const rgb = this.parseColor(value);
    if (!rgb) {
      console.warn('[a11y] Unrecognized color format:', value);
      return 'black';
    }

    const bgLum = this.calculateLuminance(rgb.r, rgb.g, rgb.b);
    const whiteLum = this.calculateLuminance(255, 255, 255);
    const blackLum = this.calculateLuminance(0, 0, 0);

    const contrastWithWhite = (whiteLum + 0.05) / (bgLum + 0.05);
    const contrastWithBlack = (bgLum + 0.05) / (blackLum + 0.05);

    // Pick whichever is HIGHER contrast
    return contrastWithWhite >= contrastWithBlack ? 'white' : 'black';
  }


  /**
   * Converts a hex color to its RGB components.
   * @param hex - The hex color string (e.g., "#RRGGBB").
   * @returns An object with the red, green, and blue components.
   */
  private hexToRgb(hex: string): { r: number, g: number, b: number } {
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    const bigint = parseInt(hex, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    };
  }

  private parseColor(input: string): { r: number, g: number, b: number } | null {
    if (!input) return null;
    const s = input.trim().toLowerCase();

    // rgb/rgba()
    const rgbMatch = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/);
    if (rgbMatch) {
      return { r: +rgbMatch[1], g: +rgbMatch[2], b: +rgbMatch[3] };
    }

    // hex: #rgb, #rgba, #rrggbb, #rrggbbaa
    const hexMatch = s.match(/^#([0-9a-f]{3,8})$/i);
    if (hexMatch) {
      let h = hexMatch[1];
      if (h.length === 3 || h.length === 4) {
        // expand shorthand
        h = h.split('').map(c => c + c).join(''); // rgb[a] -> rrggbb[aa]
      }
      // now h is 6 or 8; ignore alpha if present
      const rr = parseInt(h.slice(0, 2), 16);
      const gg = parseInt(h.slice(2, 4), 16);
      const bb = parseInt(h.slice(4, 6), 16);
      return { r: rr, g: gg, b: bb };
    }

    return null;
  }


  /**
   * Calculates the relative luminance of an RGB color.
   * @param r - The red component (0-255).
   * @param g - The green component (0-255).
   * @param b - The blue component (0-255).
   * @returns The relative luminance.
   */
  private calculateLuminance(r: number, g: number, b: number): number {
    const a = [r, g, b].map(v => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
  }

  /**
   * Converts a css variable into a hex string
   * @param variableName - The name of the variable as a string (ex: $primary, $secondary)
   * @returns The hexadecimal value of the variable.
   */
  getCssVariableValue(variableName: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  }

  /**
 * Adjusts the input color to achieve at least 3:1 contrast with the reference color.
 * Optionally returns the lightest or darkest accessible version.
 *
 * @param baseHex - The base color to adjust (hex format).
 * @param referenceHex - The background or reference color (hex format).
 * @param mode - Optional: 'lightest' | 'darkest' | 'closest' (default: 'closest')
 * @returns A new hex color string that meets 3:1 contrast.
 */
  public getContrastColor(baseHex: string, referenceHex: string, mode: 'lightest' | 'darkest' | 'closest' = 'closest'): string {
    const targetRatio = 3;
    const step = 5;
    const candidates: string[] = [];

    const tryAdjust = (direction: number) => {
      for (let i = 0; i <= 100; i += step) {
        const adjusted = this.adjustBrightness(baseHex, i * direction);
        const ratio = this.getContrastRatio(adjusted, referenceHex);
        if (ratio >= targetRatio) {
          candidates.push(adjusted);
          if (mode === 'closest') return adjusted; // early return
        }
      }
      return null;
    };

    if (mode === 'closest') {
      const result = tryAdjust(1) || tryAdjust(-1); // try lighten, then darken
      if (result) return result;
    }

    tryAdjust(1);  // lighten
    tryAdjust(-1); // darken

    if (mode === 'lightest') {
      return candidates.reduce((lightest, c) =>
        this.calculateLuminanceFromHex(c) > this.calculateLuminanceFromHex(lightest) ? c : lightest,
        baseHex
      );
    }

    if (mode === 'darkest') {
      return candidates.reduce((darkest, c) =>
        this.calculateLuminanceFromHex(c) < this.calculateLuminanceFromHex(darkest) ? c : darkest,
        baseHex
      );
    }

    // If no similar colors reach target contrast, fall back to black/white
    if (candidates.length === 0) {
      const contrastWithBlack = this.getContrastRatio('#000000', referenceHex);
      const contrastWithWhite = this.getContrastRatio('#ffffff', referenceHex);

      if (contrastWithBlack >= targetRatio && contrastWithWhite >= targetRatio) {
        return contrastWithWhite > contrastWithBlack ? '#ffffff' : '#000000';
      } else if (contrastWithWhite >= targetRatio) {
        return '#ffffff';
      } else if (contrastWithBlack >= targetRatio) {
        return '#000000';
      } else {
        console.warn('No color including black/white met contrast. Returning black.'); //literally impossible but a gal's gotta check
        return '#000000';
      }
    }

  }

  private adjustBrightness(hex: string, amount: number): string {
    const { r, g, b } = this.hexToRgb(hex);
    const clamp = (v: number) => Math.min(255, Math.max(0, v + amount));
    return this.rgbToHex({ r: clamp(r), g: clamp(g), b: clamp(b) });
  }

  private rgbToHex(rgb: { r: number; g: number; b: number }): string {
    return (
      '#' +
      [rgb.r, rgb.g, rgb.b]
        .map(v => v.toString(16).padStart(2, '0'))
        .join('')
    );
  }

  private calculateLuminanceFromHex(hex: string): number {
    const { r, g, b } = this.hexToRgb(hex);
    return this.calculateLuminance(r, g, b);
  }

  private getContrastRatio(foreground: string, background: string): number {
    const lum1 = this.calculateLuminanceFromHex(foreground);
    const lum2 = this.calculateLuminanceFromHex(background);
    const [L1, L2] = [lum1, lum2].sort((a, b) => b - a);
    return (L1 + 0.05) / (L2 + 0.05);
  }


}
